import type { Document, User } from '@graphql/generated';

export interface ChangeItem {
  type: 'name' | 'location' | 'team';
  from?: string;
  to?: string;
}

export interface ChangeGroup {
  project?: ChangeItem[];
  roles?: {
    from?: string;
    to?: string;
  };
}

export const capitalizeFirstLetter = (str: string): string => {
  if (str) return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  return '';
};

export const getTaskAssignedRole = (changes?: ChangeGroup[]): string => {
  return capitalizeFirstLetter(changes?.[0]?.roles?.to || '');
};

/** The subset of an actor an activity row needs in order to name them. */
export type ActivityActor = Pick<User, 'name' | 'firstName' | 'lastName'>;

/**
 * Display name for the actor of an activity-log entry.
 *
 * `fallback` covers both actor-less cases: a null `user` (the backend never
 * attached an actor to the audit — the project-duplication gap fixed in
 * TA-10530) and an actor object that carries no name.
 *
 * The two are deliberately NOT given different copy. Nothing in the schema
 * marks an account as deactivated — there is no `isActive`/`deactivatedAt` on
 * `User` — and `firstName`/`lastName` are `.nullable().notRequired()` for
 * active users (src/schemas/userSchema.ts), so a nameless actor cannot be
 * labelled "Inactive User" without asserting something we cannot observe.
 *
 * The caller passes the already-translated string so this stays pure and the
 * component keeps its `useTranslation` subscription to locale changes.
 */
export const getActorDisplayName = (
  user: ActivityActor | null | undefined,
  fallback: string
): string => {
  const name = user?.name?.trim();
  if (name) return name;

  const fullName = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  return fullName || fallback;
};

/**
 * TA-11048: the image-preview header (ImagePreviewHeader / ImagesPreview.web)
 * renders `previewDocuments[i].owner`, but an activity row renders its actor
 * from `audit.user`. `Document.owner` is the file's original uploader and is
 * not always the actor — e.g. task duplication `dup`s each document with its
 * original `owner_id` (and `clientId`, which is the Document cache key, so the
 * copies also share one normalized entity). The header then showed a different
 * user's avatar/initials than the row the user tapped.
 *
 * Stamp the activity actor onto each document as `owner` before it goes into
 * the preview context, mirroring ChatMessage's `owner: resolvedAuthor`. Falls
 * back to the document's own owner when the audit has no actor (removed user).
 * Only the display fields read `owner`; delete/download/markup do not.
 */
export const withActivityActorAsOwner = (
  documents: Document[],
  actor: User | null | undefined
): Document[] =>
  actor ? documents.map((doc) => ({ ...doc, owner: actor })) : documents;
